import { ChangeEvent, ComponentProps, useEffect, useState } from 'react';
import TextInput from './TextInput';
import { TimeUtilities } from './TimeUtilities';

function parseTimeStringToSeconds(time: string): number | undefined {
  const digits = (time || '').replace(/[^0-9]/g, '');
  if (!digits) return;
  const sec = parseInt(digits.slice(-2) || '0', 10);
  const min = parseInt(digits.slice(-4, -2) || '0', 10);
  const hr = parseInt(digits.slice(0, -4) || '0', 10);
  return hr * 3600 + min * 60 + sec;
}

type DurationInputProps = {
  durationSeconds?: number;

  /**
   * Callback on duration change.
   * @param duration The duration in seconds or undefined if duration is invalid or no duration is entered
   * @param isValid Differentiate an undefined duration between an invalid duration or no duration.
   * @returns
   */
  onDurationSecondsChange?: (
    duration: number | undefined,
    isValid: boolean
  ) => void;

  /** Number of digits in HH:MM:SS duration string excluding `:`. (e.g. `12:34` is 4 digits)  */
  durationStringMaxDigits?: number;

  invalidFormatErrorMessage?: string;
} & Pick<
  ComponentProps<typeof TextInput>,
  | 'labelClassName'
  | 'inputClassname'
  | 'className'
  | 'labelTitle'
  | 'placeholder'
  | 'id'
  | 'isDisabled'
  | 'isReadOnly'
  | 'hasErrors'
  | 'validationState'
  | 'errorMessage'
>;

/**
 * A component for inputting durations in the MM:SS (and HH:MM:SS) format in which the value is inputted back to front (starts from the least significant digit).
 * For example, entering 4 results in 00:04. Entering 31 results in 00:31. Entering 123 becomes 01:23. Entering 12345 results in 01:23:45.
 */
const DurationInput = ({
  durationSeconds,
  onDurationSecondsChange = () => {},
  durationStringMaxDigits = 6,
  invalidFormatErrorMessage = 'Duration must be in the format MM:SS or HH:MM:SS.',
  hasErrors = false,
  validationState,
  errorMessage,
  isReadOnly,
  ...others
}: DurationInputProps) => {
  const [timeString, setTimeString] = useState(
    TimeUtilities.convertSecondsToTimeString(durationSeconds)
  );
  const [isValid, setIsValid] = useState(true);

  // Sync timeString state to durationSeconds prop input.
  useEffect(() => {
    setTimeString((prevTimeString) => {
      const currentSeconds = parseTimeStringToSeconds(prevTimeString);

      return currentSeconds === durationSeconds
        ? prevTimeString
        : TimeUtilities.convertSecondsToTimeString(durationSeconds);
    });
  }, [durationSeconds]);

  const handleDuration = (evt: ChangeEvent<HTMLInputElement>): void => {
    const isNotNumber = /[^0-9]/g;
    const zerosPaddingStart = /^0+(?=\d)/;

    const unformattedInputStr = evt.target.value;

    if (!unformattedInputStr) {
      setTimeString('');
      onDurationSecondsChange(undefined, true);
      return;
    }

    // Preprocessing: Cleanup from say `02:31:151` to `231151` and remove invalid characters
    const trimmedDigitsStr = unformattedInputStr
      .replace(isNotNumber, '')
      .replace(zerosPaddingStart, '')
      .slice(0, durationStringMaxDigits);

    let formattedDurationStr = '';
    if (trimmedDigitsStr.length) {
      // Support either HH:MM:SS or MM:SS.
      const mask = trimmedDigitsStr.length > 4 ? '00:00:00' : '00:00';

      // Start with least significant digit and enter each digit into the mask to
      // ensure that if user enters just `10`, it becomes `00:10` (10 seconds).
      // (Ex. 231151 becomes 15:11:32)
      let i = trimmedDigitsStr.length - 1;
      const maskedStr = mask.replace(/0/g, (c) => trimmedDigitsStr[i--] ?? c);

      // Reverse the string to get actual duration string
      // (Ex. 15:11:32) becomes 32:11:51)
      formattedDurationStr = maskedStr.split('').reverse().join('');
    }

    const newDurationSeconds = parseTimeStringToSeconds(formattedDurationStr);

    const isNewDurationValid = newDurationSeconds !== undefined;
    setIsValid(isNewDurationValid);
    setTimeString(formattedDurationStr);
    onDurationSecondsChange(newDurationSeconds, isNewDurationValid);
  };

  const handleBlur = () => {
    setTimeString(TimeUtilities.convertSecondsToTimeString(durationSeconds));
  };

  return (
    <TextInput
      {...others}
      type="text"
      isReadOnly={isReadOnly}
      value={timeString}
      handleInput={handleDuration}
      hasErrors={!isValid || hasErrors}
      validationState={!isValid ? 'Error' : validationState}
      errorMessage={!isValid ? invalidFormatErrorMessage : errorMessage}
      showErrorOnBlur
      onBlur={handleBlur}
    />
  );
};

export default DurationInput;
